import { defineStore } from 'pinia'
import storage from '@/common/storage'
import { SubscriptionAPI } from '@/api'
import { useLanguageStore } from '@/stores/language'

const STORAGE_KEY_SUBSCRIPTION_INFO = 'subscriptionInfo'
const DISMISS_PREFIX = 'benefits_popup_dismissed_'

const parseDateMs = (value) => {
  if (!value) return null
  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? null : parsed
}

const MS_PER_DAY = 24 * 60 * 60 * 1000

const isLifetimeSubscription = (item) => {
  const status = Number(item?.status)
  if (![0, 1].includes(status)) {
    return false
  }
  const duration = Number(item?.duration)
  if (!Number.isFinite(duration)) {
    return false
  }
  return duration === 0
}

const calculateSubscriptionDays = (item, nowMs) => {
  const status = Number(item?.status)
  if (![0, 1].includes(status)) {
    return 0
  }
  const duration = Number(item?.duration ?? 0)
  if (status === 0) {
    return Number.isFinite(duration) ? Math.max(0, duration) : 0
  }
  const endMs = parseDateMs(item?.subscription_end_date)
  if (!endMs) {
    return Number.isFinite(duration) ? Math.max(0, duration) : 0
  }
  const remaining = Math.ceil((endMs - nowMs) / MS_PER_DAY)
  return Math.max(0, remaining)
}

const calculateValidSubscriptionDays = (info) => {
  if (!info) return 0
  const subscriptions = Array.isArray(info.active_subscriptions) ? info.active_subscriptions : []
  if (!subscriptions.length) return 0
  const nowMs = Date.now()
  return subscriptions.reduce((total, item) => total + calculateSubscriptionDays(item, nowMs), 0)
}

const hasLifetimeSubscriptionCheck = (info) => {
  if (!info) return false
  const subscriptions = Array.isArray(info.active_subscriptions) ? info.active_subscriptions : []
  return subscriptions.some((item) => isLifetimeSubscription(item))
}

const isSubscriptionDateEffective = (info) => {
  if (!info) return false
  const subscriptions = Array.isArray(info.active_subscriptions) ? info.active_subscriptions : []
  if (subscriptions.length === 0) {
    return true
  }
  const now = Date.now()
  return subscriptions.some((item) => {
    const startMs = parseDateMs(item?.subscription_start_date)
    const endMs = parseDateMs(item?.subscription_end_date)
    if (!startMs) {
      return true
    }
    if (startMs > now) {
      return false
    }
    if (endMs && endMs < now) {
      return false
    }
    return true
  })
}

export const useSubscriptionStore = defineStore('subscription', {
  state: () => ({
    subscriptionInfo: storage.get(STORAGE_KEY_SUBSCRIPTION_INFO, null),
    lastSyncedAt: null,
    products: [],
    productMap: {},
    productsLoaded: false,
    productsLoading: false,
    productsUpdatedAt: null
  }),

  getters: {
    productById: (state) => state.productMap,
    productsReady: (state) => state.productsLoaded && state.products.length > 0,

    hasActiveSubscription: (state) => {
      const info = state.subscriptionInfo
      if (!info) return false
      if (typeof info.has_active_subscription === 'boolean') {
        return info.has_active_subscription
      }
      if (typeof info.is_subscribed === 'boolean') {
        return info.is_subscribed
      }
      const subscriptions = Array.isArray(info.active_subscriptions) ? info.active_subscriptions : []
      return subscriptions.some((item) => item?.is_active)
    },

    isTrialActive: (state) => {
      return Boolean(state.subscriptionInfo?.trial_info?.is_active)
    },

    isTrialEffective() {
      return this.isTrialActive
    },

    isSubscriberEffective() {
      if (!this.hasActiveSubscription) {
        return false
      }
      return isSubscriptionDateEffective(this.subscriptionInfo)
    },

    subscriptionValidDays: (state) => {
      return calculateValidSubscriptionDays(state.subscriptionInfo)
    },

    hasLifetimeSubscription: (state) => {
      return hasLifetimeSubscriptionCheck(state.subscriptionInfo)
    },

    userTier() {
      if (this.isSubscriberEffective) {
        return 'subscriber'
      }
      if (this.isTrialEffective) {
        return 'trial'
      }
      return 'inactive'
    },

    popupVariant() {
      if (this.isSubscriberEffective) {
        return 'none'
      }
      if (this.isTrialEffective) {
        return 'full'
      }
      return 'paid'
    }
  },

  actions: {
    setProducts(products = []) {
      const list = Array.isArray(products) ? products : []
      this.products = list
      this.productMap = list.reduce((acc, item) => {
        if (item && item.product_id) {
          acc[item.product_id] = item
        }
        return acc
      }, {})
      this.productsLoaded = true
      this.productsUpdatedAt = new Date().toISOString()
    },

    getProductById(productId) {
      if (!productId) return null
      return this.productMap[productId] || null
    },

    async fetchProducts({ platform = 'web', force = false } = {}) {
      if (this.productsLoaded && !force) {
        return this.products
      }
      if (this.productsLoading) {
        return this.products
      }
      this.productsLoading = true
      try {
        const languageStore = useLanguageStore()
        const lang = languageStore?.currentUiLanguage || 'zh-CN'
        const result = await SubscriptionAPI.getProducts({ platform, lang })
        const list = Array.isArray(result?.data) ? result.data : []
        this.setProducts(list)
        return list
      } catch (error) {
        this.productsLoaded = false
        throw error
      } finally {
        this.productsLoading = false
      }
    },

    async refreshSubscriptionStatus(options = {}) {
      const { showLoading = true, showError = true } = options
      const requestConfig = { showLoading, showError }

      const result = await SubscriptionAPI.getStatus(requestConfig)
      if (result?.data) {
        this.setSubscriptionInfo(result.data)
      }
      return result?.data
    },

    setSubscriptionInfo(info) {
      const payload = info?.data ?? info
      if (payload) {
        const existingSubscriptions = this.subscriptionInfo?.active_subscriptions
        if (!payload.active_subscriptions && existingSubscriptions) {
          this.subscriptionInfo = { ...payload, active_subscriptions: existingSubscriptions }
        } else {
          this.subscriptionInfo = payload
        }
        storage.set(STORAGE_KEY_SUBSCRIPTION_INFO, this.subscriptionInfo)
      } else {
        this.subscriptionInfo = null
        storage.remove(STORAGE_KEY_SUBSCRIPTION_INFO)
      }
      this.lastSyncedAt = new Date().toISOString()
    },

    markPopupDismissed(userId) {
      if (!userId) return
      const key = `${DISMISS_PREFIX}${userId}`
      storage.set(key, true)
    },

    resetPopupDismissed(userId) {
      if (!userId) return
      const key = `${DISMISS_PREFIX}${userId}`
      storage.remove(key)
    },

    hasDismissedPopup(userId) {
      if (!userId) return false
      const key = `${DISMISS_PREFIX}${userId}`
      return Boolean(storage.get(key, false))
    },

    shouldShowPopup(userId) {
      if (!userId) return false
      if (this.popupVariant === 'none') {
        return false
      }
      return !this.hasDismissedPopup(userId)
    }
  }
})
